import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import ContactForm from '../components/ContactForm'
import SEO from '../components/SEO'
import { IT_SOLUTIONS_META } from '../data/meta'

const DEFAULT_IT_PLANS = [
  { name: 'Starter AI', price: '25,000', period: 'one time', badge: '', featured: false, features: ['AI-powered single page website', 'Smart responsive design', 'AI chatbot for lead capture', '1 round of revisions', '30 days support'], notIncluded: ['CMS integration', 'E-commerce', 'Mobile app'] },
  { name: 'Growth AI', price: '75,000', period: 'one time', badge: 'Most Popular', featured: true, features: ['AI-enhanced up to 5 page website', 'Predictive SEO optimization', 'Basic mobile app with AI features', 'Payment gateway + AI fraud detection', '3 months maintenance', 'Custom AI automation'], notIncluded: ['Advanced analytics'] },
  { name: 'Enterprise AI', price: '1,50,000+', period: 'custom quote', badge: '', featured: false, features: ['Full-stack AI web application', 'Native mobile apps with AI', 'AI-powered e-commerce platform', 'Custom AI integrations & APIs', 'Advanced AI analytics dashboard', '6 months maintenance & support', 'Dedicated project manager'], notIncluded: [] }
]

const encodeEmail = (local, domain) => {
  const email = `${local}@${domain}`
  const labels = email.split('').map((c) => `&#${c.charCodeAt(0)};`).join('')
  return { mailto: email, label: labels }
}

export default function ItSolutions() {
  const location = useLocation()
  const supportEmail = encodeEmail('support', 'tredextechnology.in')
  const [pricing, setPricing] = useState({ symbol: '₹', plans: DEFAULT_IT_PLANS })

  useEffect(() => {
    let cancelled = false
    fetch('/api/pricing')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data || !data.plans) return
        setPricing({ symbol: data.symbol || '₹', plans: data.plans })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1)
      const element = document.getElementById(id)
      if (element) {
        const navHeight = 80
        const elementPosition = element.getBoundingClientRect().top + window.scrollY - navHeight
        setTimeout(() => {
          window.scrollTo({ top: elementPosition, behavior: 'smooth' })
        }, 100)
      }
    }
  }, [location])

  const services = [
    { icon: 'fa-laptop-code', title: 'AI-Powered Website Development', desc: 'Custom, responsive websites with built-in AI chatbots, smart recommendations, and automated customer engagement.' },
    { icon: 'fa-mobile-alt', title: 'Intelligent App Development', desc: 'Native and cross-platform mobile apps infused with AI capabilities like voice assistants, predictive analytics, and automation.' },
    { icon: 'fa-search', title: 'AI-Driven SEO Optimization', desc: 'On-page and technical SEO powered by AI tools for keyword discovery, content optimization, and ranking improvement.' },
    { icon: 'fa-eye-slash', title: 'AI Site Analysis & Audits', desc: 'In-depth analysis of your site’s performance, usability, and SEO health using AI-driven insights with a clear improvement roadmap.' },
    { icon: 'fa-redo', title: 'AI-Enhanced Website Rebuild', desc: 'Complete overhaul of an existing website with AI integrations, modern design, smart automation, and improved performance.' },
    { icon: 'fa-shopping-cart', title: 'Smart E-Commerce Solution', desc: 'Scalable online stores with AI-powered product recommendations, dynamic pricing, chatbots, and frictionless checkout.' },
    { icon: 'fa-brain', title: 'Custom AI Feature Integration', desc: 'Integrate intelligent chatbots, recommendation engines, automation workflows, computer vision, and other AI capabilities to enhance engagement and drive results.' },
  ]

  const steps = [
    { icon: 'fa-search', title: 'Discovery', desc: 'We analyze your business goals, audience, and current digital presence to identify opportunities.' },
    { icon: 'fa-pencil-ruler', title: 'Design', desc: 'Our team crafts wireframes and UI designs that align with your brand and user expectations.' },
    { icon: 'fa-code', title: 'Development', desc: 'We build using modern tech stacks, ensuring scalability, security, and performance from day one.' },
    { icon: 'fa-rocket', title: 'Launch', desc: 'After rigorous testing, we deploy your solution and monitor for a smooth go-live experience.' },
    { icon: 'fa-chart-line', title: 'Optimize', desc: 'Post-launch, we track metrics, gather feedback, and continuously improve your digital product.' },
  ]

  const testimonials = [
    { name: 'Rajesh Kumar', business: 'Owner, Kumar Electronics', text: 'Tredex completely revamped our website. Our online inquiries have doubled since the new site went live.', rating: 5 },
    { name: 'Priya Sharma', business: 'Founder, Glow Beauty Salon', text: 'The e-commerce solution they built for us is seamless. Payments, inventory, and shipping all work flawlessly.', rating: 5 },
    { name: 'Arun Patel', business: 'Director, Patel Real Estate', text: 'Their SEO expertise moved us to the first page of Google. The leads have been incredible.', rating: 5 },
    { name: 'Sneha Reddy', business: 'Owner, FreshBite Restaurant', text: 'The mobile app they developed for us increased repeat orders by 60%. Highly recommend their team.', rating: 5 },
  ]

  return (
    <>
      <SEO
        title={IT_SOLUTIONS_META.title}
        description={IT_SOLUTIONS_META.description}
        canonical={IT_SOLUTIONS_META.canonical}
        keywords={IT_SOLUTIONS_META.keywords}
        structuredData={IT_SOLUTIONS_META.structuredData}
      />
      <Navbar />
      <section className="it-solutions-hero">
        <div className="container it-hero-content">
          <div className="it-hero-text">
            <span className="hero-badge">✦ &nbsp; AI-Powered Software & IT Services &nbsp; ✦</span>
            <h1 className="hero-title">
              We Build <span className="gradient-text">AI-Driven Digital Experiences</span> That Grow Your Business
            </h1>
            <p className="hero-description">
              From intelligent websites to smart applications, our team delivers end-to-end IT solutions powered by AI. We turn complex problems into elegant, scalable software that thinks ahead.
            </p>
            <div className="hero-buttons">
                                          <Link to="/contact" className="btn btn-primary btn-lg">
                Start Your Project <i className="fas fa-arrow-right"></i>
              </Link>
              <Link to="/services" className="btn btn-outline btn-lg">
                View All Services <i className="fas fa-th-large"></i>
              </Link>
            </div>
            <div className="hero-stats">
              {[
                { value: '100%', label: 'On Time' },
                { value: '40%', label: 'Avg. Traffic Increase' },
                { value: '60%', label: 'Less Support Load' },
              ].map((stat, i) => (
                <div className="stat-item" key={i}>
                  <span className="stat-number">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="it-hero-visual">
            <div className="it-code-window">
              <div className="it-code-header">
                <div className="it-code-dots">
                  <span></span><span></span><span></span>
                </div>
                <div className="it-code-title">project.config.js</div>
              </div>
              <div className="it-code-body">
                <div><span className="it-code-key">const</span> <span className="it-code-var">project</span> = {'{'}</div>
                <div>&nbsp;&nbsp;<span className="it-code-key">type</span>: <span className="it-code-string">'full-stack'</span>,</div>
                <div>&nbsp;&nbsp;<span className="it-code-key">stack</span>: [<span className="it-code-string">'React'</span>, <span className="it-code-string">'Node'</span>, <span className="it-code-string">'SQL'</span>],</div>
                <div>&nbsp;&nbsp;<span className="it-code-key">performance</span>: <span className="it-code-number">98</span>,</div>
                <div>&nbsp;&nbsp;<span className="it-code-key">uptime</span>: <span className="it-code-string">'99.9%'</span>,</div>
                <div>&nbsp;&nbsp;<span className="it-code-key">support</span>: <span className="it-code-string">'24/7'</span>,</div>
                <div>{'}'}</div>
                <div>&nbsp;&nbsp;</div>
                <div><span className="it-code-key">export default</span> <span className="it-code-var">project</span>;</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="section-problem">
        <div className="container">
          <div className="problem-grid">
            <div className="problem-card problem-bad">
              <div className="problem-icon"><i className="fas fa-times-circle"></i></div>
              <h3>The Problem</h3>
              <p>Your business deserves a digital presence that works as hard as you do. But most websites and apps fall short without intelligence.</p>
              <ul className="problem-list">
                <li><i className="fas fa-times"></i> Static websites that don't engage visitors</li>
                <li><i className="fas fa-times"></i> Apps without smart automation or insights</li>
                <li><i className="fas fa-times"></i> No AI visibility on Google or user personalization</li>
                <li><i className="fas fa-times"></i> Manual processes that waste time and miss leads</li>
              </ul>
            </div>
            <div className="problem-divider">
              <div className="problem-divider-line"></div>
              <div className="problem-divider-icon"><i className="fas fa-arrow-right"></i></div>
              <div className="problem-divider-line"></div>
            </div>
            <div className="problem-card problem-good">
              <div className="problem-icon"><i className="fas fa-check-circle"></i></div>
              <h3>The Tredex Solution</h3>
              <p>We design, build, and optimize AI-powered software that solves real business problems. From concept to deployment and beyond.</p>
              <ul className="problem-list">
                <li><i className="fas fa-check"></i> AI-driven websites that convert and engage</li>
                <li><i className="fas fa-check"></i> Smart mobile apps with predictive features</li>
                <li><i className="fas fa-check"></i> AI strategies that rank and personalize</li>
                <li><i className="fas fa-check"></i> Intelligent automation that saves hours every week</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-overview">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Services</span>
            <h2 className="section-title">Complete <span className="gradient-text">AI-Powered IT Solutions</span></h2>
            <p className="section-description">
              We handle everything from strategy and design to development, deployment, and ongoing AI optimization.
            </p>
          </div>
          <div className="services-grid it-services-grid">
            {services.map((service, i) => (
              <div className="service-card" key={i}>
                <div className="service-icon"><i className={`fas ${service.icon}`}></i></div>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IT Solutions Pricing Section */}
      <section id="it-pricing" className="it-pricing-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">AI-Powered IT Pricing</span>
            <h2 className="section-title">Transparent <span className="gradient-text">AI-Enhanced Packages</span></h2>
            <p className="section-description">
              Fixed-price packages for AI-powered websites, smart apps, and intelligent digital solutions. Every plan includes core AI capabilities.
            </p>
          </div>
          <div className="it-pricing-grid">
             {pricing.plans.map((plan, i) => {
               const base = DEFAULT_IT_PLANS[i] || DEFAULT_IT_PLANS[0]
               return (
               <div className={`it-pricing-card ${base.featured ? 'featured' : ''}`} key={i}>
                 {base.badge && <div className="it-pricing-badge">{base.badge}</div>}
                 <h3>{base.name}</h3>
                 <div className="it-pricing-amount">{pricing.symbol}{plan.price}</div>
                 <div className="it-pricing-period">{base.period}</div>
                 <ul className="it-pricing-features">
                   {base.features.map((f, j) => (
                     <li key={j}><i className="fas fa-check"></i> {f}</li>
                   ))}
                   {base.notIncluded.map((f, j) => (
                     <li key={j} className="it-pricing-not-included"><i className="fas fa-times"></i> {f}</li>
                   ))}
                 </ul>
                                 <Link to="/contact" className={`btn ${base.featured ? 'btn-primary' : 'btn-outline'} btn-block`}>
                   Get Quote <i className="fas fa-arrow-right"></i>
                 </Link>
               </div>
             )})}
          </div>

          {/* AI Features Section */}
          <div className="ai-addon-section">
            <div className="ai-addon-header">
              <div className="ai-addon-icon"><i className="fas fa-brain"></i></div>
              <div>
                <h3>Built-In AI Features</h3>
                <p>Every IT package comes with intelligent AI capabilities out of the box. No extra cost for core AI features.</p>
              </div>
            </div>
            <div className="ai-addon-grid">
              {[
                { name: 'AI Chatbot', desc: '24/7 automated customer support on your website or app' },
                { name: 'Smart Search', desc: 'AI-powered search that understands natural language' },
                { name: 'Lead Qualifier', desc: 'Automatically score and route high-value leads' },
                { name: 'Recommendation Engine', desc: 'Personalized product/content suggestions for users' },
                { name: 'Automation Workflows', desc: 'Smart automation that saves hours every week' },
                { name: 'Analytics Insights', desc: 'AI-driven reports that tell you what to fix next' },
              ].map((addon, i) => (
                <div className="ai-addon-card" key={i}>
                  <div className="ai-addon-name">{addon.name}</div>
                  <div className="ai-addon-desc">{addon.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our AI-Driven Process</span>
            <h2 className="section-title">How We <span className="gradient-text">Deliver Intelligent Results</span></h2>
            <p className="section-description">A proven 5-step process infused with AI tools that ensures your project is delivered on time, on budget, and exceeds expectations.</p>
          </div>
          <div className="steps-grid">
            {steps.map((step, i) => (
              <div className="step-item" key={i}>
                <div className="step-number">{i + 1}</div>
                <div className="step-icon"><i className={`fas ${step.icon}`}></i></div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Testimonials</span>
            <h2 className="section-title">Trusted by <span className="gradient-text">Business Owners</span></h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, i) => (
              <div className="testimonial-card" key={i}>
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <i key={j} className="fas fa-star"></i>
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar"><i className="fas fa-user"></i></div>
                  <div>
                    <strong>{testimonial.name}</strong>
                    <span className="testimonial-business">{testimonial.business}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-wrapper">
            <div className="cta-content">
              <span className="section-badge">Get Started</span>
              <h2 className="cta-title">Ready to Build Something <span className="gradient-text">Intelligent</span>?</h2>
              <p className="cta-description">Tell us about your project. We'll get back to you within 24 hours with a free AI consultation and proposal.</p>
              <div className="cta-buttons">
                                 <Link to="/contact" className="btn btn-primary btn-lg">Start Your AI Project <i className="fas fa-paper-plane"></i></Link>
                <a href="#it-pricing" className="btn btn-outline btn-lg">View AI Pricing <i className="fas fa-list"></i></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Contact Us</span>
            <h2 className="section-title">Let's Discuss Your <span className="gradient-text">AI Project</span></h2>
            <p className="section-description">Tell us about your business and AI needs. We'll send you a free proposal within 24 hours.</p>
          </div>
          <div className="contact-wrapper">
            <div className="contact-info">
              <div className="contact-info-card">
                <div className="contact-info-icon"><i className="fas fa-envelope"></i></div>
                <h4>Email Us</h4>
                <a href={`mailto:${supportEmail.mailto}`} dangerouslySetInnerHTML={{ __html: supportEmail.label }} />
              </div>
              <div className="contact-info-card">
                <div className="contact-info-icon"><i className="fas fa-map-marker-alt"></i></div>
                <h4>Our HQ</h4>
                <p>Chennai, Tamil Nadu, India</p>
              </div>
          
            </div>
            <div className="contact-form-wrapper">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}
